import * as React from "react";
import * as ReactDOM from "react-dom";
import Loader from "msem-lib/es/components/loader";
import Widget from "./components/widget/widget";
import UnsupportedBrowser from "./components/unsupported-browser";

import "@formatjs/intl-pluralrules/polyfill";
import "@formatjs/intl-pluralrules/locale-data/fr";
import "@formatjs/intl-pluralrules/locale-data/en";

let tunnelRef;

const THEME_ID = "msem-theme";
const DEFAULT_THEME = `${process.env.PUBLIC_URL}/themes/MseM.css`;
const DEFAULT_WIDGET_CONFIG = {
  refreshOnLodgingChange: false,
};

const injectTheme = (url = DEFAULT_THEME) => {
  const theme = document.getElementById(THEME_ID);
  if (theme) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  link.id = THEME_ID;
  document.head.appendChild(link);
};

const createParent = (parentId) => {
  const el = document.createElement("div");
  el.id = parentId;
  if (parentId === "MseM-tunnel") {
    document.body.insertBefore(el, document.body.firstChild);
  } else {
    document.body.appendChild(el);
  }
  return el;
};

const findParent = (parentId, options) => {
  const { groundedTo } = options;
  const targetSelector = groundedTo ?? `#${parentId}`;
  const target = document.querySelector(targetSelector);
  return target ?? createParent(parentId);
};

const readFromUrl = (name) => {
  const params = new URL(document.location.href).searchParams;
  const value = params.get(name);
  return value || undefined;
};

const optionsPatch = (name, value) => {
  return value !== undefined ? { [name]: value } : {};
};

const stringFromUrl = (name) => {
  const param = readFromUrl(name);
  return optionsPatch(name, param);
};

const numberFromUrl = (name) => {
  const param = readFromUrl(name);
  const value = param ? Number(param) : undefined;
  return optionsPatch(name, value);
};

const langFromUrl = () => {
  const lang = readFromUrl("lang");
  const language = readFromUrl("language");
  const result = lang || language;
  return result !== undefined ? { lang: result.toLowerCase().indexOf("fr") === 0 ? "fr" : "en" } : {};
};

const booleanFromUrl = (name) => {
  const param = readFromUrl(name);
  const value = param ? param === "true" : undefined;
  return optionsPatch(name, value);
};

const isBrowserSupported = () => {
  // minimum level of requirements : CSS Custom Properties support
  const supportCssCustomProps = window.CSS && CSS.supports("color", "var(--primary)");
  if (!supportCssCustomProps) return false;

  // Except on Mac / iOS : Weird fetch bugs below Safari 11
  // We detect it as Apple vendor + dynamic import support (landed in Safari 11)
  const apple = navigator.vendor.toLocaleLowerCase().indexOf("apple") !== -1;
  if (!apple) return true;
  try {
    // eslint-disable-next-line no-new-func
    new Function('import("")');
    return true;
  } catch (err) {
    return false;
  }
};

const browserWarning = (options, parentId) => {
  const container = findParent(parentId, options);
  ReactDOM.render(<UnsupportedBrowser options={options} />, container);
};

const setBodyOverflow = (value) => {
  document.body.style.overflow = value;
};

const open = (parentId, widget, options, presets = {}, config = DEFAULT_WIDGET_CONFIG) => {
  const container = findParent(parentId, options);
  const floating = options.groundedTo === undefined;
  const KindWidget = widget;
  const paramsOption = {
    ...options,
    lang: options.lang === "fr" || options.lang === undefined ? "fr" : "en",
    facet: options.facet ?? 0,
  };
  const overridenOptions = {
    ...paramsOption,
    ...langFromUrl(),
    ...numberFromUrl("facet"),
    ...numberFromUrl("hoteid"),
    ...numberFromUrl("resort"),
    ...stringFromUrl("channel"),
    ...booleanFromUrl("preview"),
    ...booleanFromUrl("debugXML"),
    ...stringFromUrl("partenaire"),
    ...booleanFromUrl("isGescoOperator"),
  };

  injectTheme(options.theme);

  if (floating && !options.draggable) {
    setBodyOverflow("hidden");
  }

  const refreshTunnel = () => {
    if (tunnelRef) {
      const [tree, container] = tunnelRef;
      ReactDOM.unmountComponentAtNode(container);
      ReactDOM.render(tree, container);
    }
  };

  const close = () => {
    if (!tunnelRef && floating) {
      setBodyOverflow("");
    } else {
      const [, tunnelContainer, tunnelFloating] = tunnelRef;
      const isTunnel = tunnelContainer === container;
      if (floating && (isTunnel || !tunnelFloating)) {
        setBodyOverflow("");
      }
      if (isTunnel) {
        tunnelRef = undefined;
      } else {
        refreshTunnel();
      }
    }
    setTimeout(() => {
      ReactDOM.unmountComponentAtNode(container);
    }, 100);
  };

  const tree = (
    <React.StrictMode>
      <Widget
        options={overridenOptions}
        presets={presets}
        refreshTunnel={refreshTunnel}
        close={floating ? close : undefined}
        config={config}
      >
        <React.Suspense fallback={<Loader />}>
          <KindWidget options={overridenOptions} presets={presets} />
        </React.Suspense>
      </Widget>
    </React.StrictMode>
  );
  ReactDOM.unmountComponentAtNode(container);
  ReactDOM.render(tree, container);

  return [tree, container, floating];
};

const openIfSupported = (widget, parentId, options, presets, config) => {
  if (!isBrowserSupported()) return browserWarning(options, parentId);
  return open(parentId, widget, options, presets, config);
};

const lodging = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "lodging" */ "./widgets/lodging-widget")),
    "MseM-lodging",
    options,
    presets
  );
};

const skiRental = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "ski-rental" */ "./widgets/ski-rental-widget")),
    "MseM-skiRental",
    options,
    presets,
    { refreshOnLodgingChange: true }
  );
};

const skiPass = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "ski-pass" */ "./widgets/ski-pass-widget")),
    "MseM-skiPass",
    options,
    presets
  );
};

const esf = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "esf" */ "./widgets/esf-widget")),
    "MseM-esf",
    options,
    presets
  );
};

const tunnel = (options, presets) => {
  tunnelRef = openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "tunnel" */ "./widgets/tunnel-widget")),
    "MseM-tunnel",
    options,
    presets
  );
};

const otherProducts = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "other-products" */ "./widgets/other-products-widget")),
    "MseM-otherProducts",
    options,
    presets,
    { refreshOnLodgingChange: true }
  );
};

const transfer = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "transfer" */ "./widgets/transfer-widget")),
    "MseM-transfer",
    { ...options, mode: "PRIVATE_TRANSFER" },
    presets
  );
};

const excursion = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "transfer" */ "./widgets/transfer-widget")),
    "MseM-transfer",
    { ...options, mode: "EXCURSION" },
    presets
  );
};

const shuttle = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "transfer" */ "./widgets/transfer-widget")),
    "MseM-transfer",
    { ...options, mode: "SHUTTLE" },
    presets
  );
};

const genericLine = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "generic-line" */ "./widgets/generic-line-widget")),
    "MseM-genericLine",
    options,
    presets
  );
};

const esfCustom = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "esf-custom" */ "./widgets/esf-custom-widget")),
    "MseM-esfCustom",
    options,
    presets
  );
};

const vakario = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "vakario" */ "./widgets/vakario-widget")),
    "MseM-vakario",
    options,
    presets
  );
};

const widgetPackage = (options, presets = {}) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "package" */ "./widgets/package-widget")),
    "MseM-package",
    options,
    presets
  );
};

const customerAccount = (options, presets) => {
  openIfSupported(
    React.lazy(() => import(/* webpackChunkName: "customerAccount" */ "./widgets/customer-account-widget")),
    "MseM-customer-account",
    options,
    presets
  );
};

window.MseM = {
  ...window.MseM,
  lodging,
  skiRental,
  skiPass,
  esf,
  tunnel,
  otherProducts,
  transfer,
  excursion,
  shuttle,
  genericLine,
  esfCustom,
  vakario,
  widgetPackage,
  customerAccount,
};
